/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExclamationTriangleIcon, Text, UnstyledButton } from '@/shared/ui';
import { SectionCard } from '@features/property-management/components/PropertyDetailView/SectionCard';
import type { ClientRow } from '../../context/ClientManagementContext';
import { clientQuality, type ClientFormSection } from './client-quality';

/**
 * How many missing fields the card shows before folding the rest away.
 *
 * Three, because the card sits in a rail beside the page's own content:
 * a registration missing eleven fields listed all eleven and became the
 * tallest thing on the screen, pushing the cards under it off the fold.
 * Three is enough to show what kind of gap it is and to start on it.
 */
const VISIBLE_MISSING = 3;

export interface RegistrationQualityCardProps {
  client: ClientRow;
  /** Opens the edit form, on the named section when one is given. */
  onEdit: (section?: ClientFormSection) => void;
}

/**
 * How complete the registration is, and every field still missing as a
 * button that opens the edit form.
 *
 * The percentage on its own was a grade with nothing to do about it. The
 * missing fields are the actionable half, so each is a row that takes the
 * broker straight to filling it.
 *
 * @param props - The client and the edit callback.
 * @returns The card.
 */
export function RegistrationQualityCard({ client, onEdit }: RegistrationQualityCardProps) {
  const { t } = useTranslation('features/clients');
  const { percent, missing } = clientQuality(client);
  const [expanded, setExpanded] = useState(false);
  const hidden = missing.length - VISIBLE_MISSING;
  const shown = expanded ? missing : missing.slice(0, VISIBLE_MISSING);

  return (
    <SectionCard
      data-testid="client-quality-card"
      title={t('detail.quality.title')}
      titleAccessory={(
        <Text
          component="span"
          className="whitespace-nowrap text-[length:var(--text-meta)] [font-weight:var(--font-bold)] tabular-nums text-(--seed-base)"
        >
          {percent}%
        </Text>
      )}
    >
      <div className="mb-(--space-3) h-[6px] overflow-hidden rounded-[3px] bg-(--surface-tertiary)">
        <div
          className="h-full rounded-[3px] bg-(--seed-base)"
          style={{ width: `${percent}%` }}
        />
      </div>

      <Text
        className="m-0 mb-(--space-2-5) block text-[length:var(--text-meta)] leading-[var(--leading-normal)] text-(--text-tertiary-soft)"
      >
        {missing.length === 0
          ? t('detail.quality.complete')
          : t('detail.quality.missingCount', { count: missing.length })}
      </Text>

      {missing.length > 0 && (
        <div className="flex flex-col gap-(--space-2)">
          {shown.map((field) => (
            <UnstyledButton
              key={field.id}
              type="button"
              data-testid={`client-quality-missing-${field.id}`}
              onClick={() => onEdit(field.section)}
              className="box-border flex w-full items-center gap-(--space-2) rounded-(--radius-md) border border-solid border-(--border-subtle) bg-(--surface-primary) px-(--space-2-5) py-(--space-2) text-left cursor-pointer hover:bg-(--surface-hover)"
            >
              <ExclamationTriangleIcon
                width={13}
                height={13}
                className="flex-none text-(--accent-cobre)"
              />
              <Text
                component="span"
                className="min-w-0 flex-1 text-[length:var(--text-meta)] text-(--text-primary)"
              >
                {t(field.labelKey)}
              </Text>
              <Text
                component="span"
                className="flex-none text-[length:var(--text-meta)] [font-weight:var(--font-semibold)] text-(--seed-base)"
              >
                {t('detail.quality.fill')}
              </Text>
            </UnstyledButton>
          ))}

          {hidden > 0 && (
            <UnstyledButton
              type="button"
              data-testid="client-quality-toggle"
              aria-expanded={expanded}
              onClick={() => setExpanded((open) => !open)}
              className="self-start border-none bg-transparent p-(--space-1) text-[length:var(--text-meta)] [font-weight:var(--font-semibold)] text-(--seed-base) cursor-pointer hover:underline"
            >
              {expanded
                ? t('detail.quality.showLess')
                : t('detail.quality.showMore', { count: hidden })}
            </UnstyledButton>
          )}
        </div>
      )}
    </SectionCard>
  );
}
